import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check, ChevronRight, Compass, ListChecks, Sparkles, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePathScope } from '../context/PathScopeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChoosePathPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const { scope, pathLabel, applyPreset, applySituations, clearScope } = usePathScope();

  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [options, setOptions] = useState({ situations: [], goals: [], urgency: [] });
  const [saving, setSaving] = useState(null);
  const [mode, setMode] = useState('quick');
  const [selected, setSelected] = useState([]);
  const [goal, setGoal] = useState('');
  const [urgency, setUrgency] = useState('');
  const [dropdownKey, setDropdownKey] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [p, o] = await Promise.all([
          axios.get(`${API}/state/presets`, getAuthHeader()),
          axios.get(`${API}/state/options`, getAuthHeader()),
        ]);
        setPresets(p.data.presets || []);
        setGroups(p.data.groups || []);
        setOptions(o.data || { situations: [], goals: [], urgency: [] });
      } catch {
        toast.error('Could not load the path options. Refresh and try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [getAuthHeader]);

  useEffect(() => {
    if (scope?.state) {
      setSelected(scope.state.situations || []);
      setGoal(scope.state.goal || '');
      setUrgency(scope.state.urgency || '');
      setDropdownKey(scope.preset_key && scope.preset_key !== 'custom' ? scope.preset_key : '');
    }
  }, [scope]);

  const grouped = useMemo(() => {
    const map = {};
    groups.forEach(g => { map[g] = presets.filter(p => p.group === g); });
    return map;
  }, [groups, presets]);

  const situationGroups = useMemo(() => {
    const map = {};
    (options.situations || []).forEach(s => {
      map[s.group] = map[s.group] || [];
      map[s.group].push(s);
    });
    return map;
  }, [options.situations]);

  const choosePreset = async (key) => {
    setSaving(key);
    try {
      const res = await applyPreset(key);
      toast.success(`Your FuturePath is now set up for: ${res.preset_label}`);
      navigate('/app/dashboard');
    } catch {
      toast.error('Could not save your path. Try again.');
    } finally {
      setSaving(null);
    }
  };

  const saveCustom = async () => {
    if (selected.length === 0) {
      toast.error('Pick at least one thing that describes your situation.');
      return;
    }
    setSaving('custom');
    try {
      await applySituations({ situations: selected, goal: goal || null, urgency: urgency || null, constraints: [] });
      toast.success('Your FuturePath has been reconfigured.');
      navigate('/app/dashboard');
    } catch {
      toast.error('Could not save your answers. Try again.');
    } finally {
      setSaving(null);
    }
  };

  const resetScope = async () => {
    await clearScope();
    toast.success('Showing the full platform again.');
  };

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  return (
    <AppLayout>
      <PageHeader
        title="Choose Your Path"
        description="Tell us where you are and FuturePath reconfigures itself around you, only the tools your situation actually needs."
      />

      {pathLabel && (
        <GlassCard className="mb-6" data-testid="current-path-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/10"><Compass className="w-5 h-5 text-cyan-300" /></div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-slate-300">Your current path</p>
              <p className="text-white font-semibold" data-testid="current-path-label">{pathLabel}</p>
              <p className="text-xs text-slate-300 mt-1">
                {(scope?.archetypes || []).map(a => a.label).join(' · ')}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={resetScope} data-testid="reset-path-btn">
              <RotateCcw className="w-4 h-4 mr-1" /> Show everything again
            </Button>
          </div>
          <p className="text-xs text-slate-300 mt-3">
            Tools outside this path aren't removed, they're switched off. Find them any time under
            <span className="text-cyan-300"> "More tools you may like"</span> in the sidebar, and turn on whatever you want to keep.
          </p>
        </GlassCard>
      )}

      <div className="flex gap-1 mb-6 bg-slate-800/50 rounded-lg p-1 w-fit" data-testid="choose-path-tabs">
        <button onClick={() => setMode('quick')} data-testid="tab-quick"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'quick' ? 'bg-slate-700 text-white' : 'text-slate-300 hover:text-slate-200'}`}>
          <Sparkles className="w-4 h-4" /> Quick pick
        </button>
        <button onClick={() => setMode('custom')} data-testid="tab-custom"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'custom' ? 'bg-slate-700 text-white' : 'text-slate-300 hover:text-slate-200'}`}>
          <ListChecks className="w-4 h-4" /> Answer a few questions
        </button>
      </div>

      {mode === 'quick' && (
        <div className="space-y-8">
          {/* Dropdown shortcut */}
          <GlassCard>
            <label className="block text-sm font-medium text-white mb-2">Pick from the list</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={dropdownKey}
                onChange={(e) => setDropdownKey(e.target.value)}
                data-testid="path-dropdown"
                className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500"
              >
                <option value="">Select what's happening in your life right now…</option>
                {groups.map(g => (
                  <optgroup key={g} label={g}>
                    {(grouped[g] || []).map(p => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <Button onClick={() => choosePreset(dropdownKey)} disabled={!dropdownKey || !!saving} data-testid="apply-dropdown-btn">
                {saving === dropdownKey ? 'Setting up…' : <>Set up my FuturePath <ChevronRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
            <p className="text-xs text-slate-300 mt-2">You can change this at any time, nothing is locked in.</p>
          </GlassCard>

          {/* Cards */}
          {groups.map(g => (
            <div key={g}>
              <h3 className="text-lg font-semibold text-white mb-3">{g}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(grouped[g] || []).map(p => {
                  const active = scope?.preset_key === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => choosePreset(p.key)}
                      disabled={!!saving}
                      data-testid={`preset-${p.key}`}
                      className={`text-left p-4 rounded-xl border transition-all hover:-translate-y-0.5 disabled:opacity-60 ${active ? 'border-cyan-400/60 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/40 hover:border-slate-500'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-white">{p.label}</p>
                        {active && <Check className="w-4 h-4 text-cyan-300 shrink-0" />}
                      </div>
                      <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">{p.blurb}</p>
                      <p className="text-[11px] text-slate-300 mt-2">
                        {(p.archetypes || []).map(a => a.label).join(' · ')}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'custom' && (
        <div className="space-y-6">
          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-1">What's happening in your life right now?</h3>
            <p className="text-sm text-slate-300 mb-4">Select everything that applies, you can be more than one thing at once.</p>
            {Object.entries(situationGroups).map(([group, items]) => (
              <div key={group} className="mb-5">
                <p className="text-xs uppercase tracking-wider text-slate-300 mb-2">{group}</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {items.map(s => {
                    const on = selected.includes(s.key);
                    return (
                      <button
                        key={s.key}
                        onClick={() => setSelected(prev => on ? prev.filter(x => x !== s.key) : [...prev, s.key])}
                        data-testid={`situation-${s.key}`}
                        className={`flex items-center gap-2 text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${on ? 'border-cyan-400/60 bg-cyan-500/10 text-white' : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-500'}`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-cyan-400 border-cyan-400' : 'border-slate-500'}`}>
                          {on && <Check className="w-3 h-3 text-slate-900" />}
                        </span>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </GlassCard>

          <div className="grid md:grid-cols-2 gap-6">
            <GlassCard>
              <h3 className="text-base font-semibold text-white mb-3">What are you trying to accomplish?</h3>
              <select value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="goal-select"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500">
                <option value="">Choose one…</option>
                {(options.goals || []).map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </GlassCard>
            <GlassCard>
              <h3 className="text-base font-semibold text-white mb-3">How soon do you need to move?</h3>
              <select value={urgency} onChange={(e) => setUrgency(e.target.value)} data-testid="urgency-select"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500">
                <option value="">Choose one…</option>
                {(options.urgency || []).map(u => <option key={u.key} value={u.key}>{u.label}</option>)}
              </select>
            </GlassCard>
          </div>

          <Button onClick={saveCustom} disabled={!!saving} data-testid="save-custom-btn">
            {saving === 'custom' ? 'Setting up…' : <>Reconfigure my FuturePath <ChevronRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
      )}
    </AppLayout>
  );
}
